import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Heading,
  HStack,
  Pressable,
  Spinner,
  Text,
  useDisclose,
  VStack,
} from "native-base";
import { Helmet } from "react-helmet-async";
import qs from "qs";
import moment from "moment";
import { MdArrowRight } from "react-icons/md";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import colors from "../../../../constants/colors";
import { StateContext, StatusContext } from "../../../../contexts";
import API from "../../../../api";
import { formatToCurrency, getStatus, getStatusColor } from "../../../../utils";
import Modal from "../../../../components/modal";
import PaymentModal from "../../../../components/paymentModal";
import routes from "../../../../routes";

function getType(movement) {
  if (movement.amount > 0) {
    return "RECARGA DE SALDO";
  }
  if (movement.tourTransaction) {
    return `ABONO A VIAJE CON FOLIO ${movement.tourTransaction.tour.id}`;
  }
  if (movement.amount < 0) {
    return "DESCUENTO DE SALDO";
  }
}

function AgencyProfileBalancePage() {
  const state = useContext(StateContext);
  const status = useContext(StatusContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoadingBalance, setIsLoadingBalance] = useState(true);
  const [isLoadingMovements, setIsLoadingMovements] = useState(true);
  const [balance, setBalance] = useState(0);
  const [movements, setMovements] = useState([]);
  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclose(searchParams.get("modal") === "true");
  const {
    isOpen: isRedirectModalOpen,
    onOpen: onRedirectModalOpen,
    onClose: onRedirectModalClose,
  } = useDisclose();

  const getMovements = useCallback(async () => {
    setIsLoadingMovements(true);
    const query = qs.stringify({
      filters: { agency: state.user.agency.id },
      populate: { tourTransaction: { populate: { tour: true } } },
      sort: "id:desc",
    });
    const {
      data: { data },
    } = await API(state.jwt).get(`/agency-movements?${query}`);
    setMovements(data);
    setIsLoadingMovements(false);
  }, [state.jwt, state.user.agency.id]);

  const getBalance = useCallback(async () => {
    try {
      setIsLoadingBalance(true);
      const {
        data: {
          data: { balance: _balance },
        },
      } = await API(state.jwt).get("/agency-movements/balance");
      setBalance(_balance);
      setIsLoadingBalance(false);
      await getMovements();
    } catch (error) {
      setIsLoadingBalance(false);
      setIsLoadingMovements(false);
      status.updateErrorMessage(error);
    }
  }, [getMovements, state.jwt, status]);

  useEffect(() => {
    getBalance();
  }, [getBalance]);

  const onRecharge = async (type, amount, _file, onError) => {
    try {
      if (type === "card") {
        const { data } = await API(state.jwt).post(
          "/agency-movements/recharge",
          { amount },
        );
        window.open(data.url, "_blank", "noopener,noreferrer");
      } else if (type === "transfer") {
        const formData = new FormData();
        formData.append("files", _file);
        const {
          data: [file],
        } = await API(state.jwt).post("/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        await API(state.jwt).post("/agency-movements", {
          data: {
            agency: state.user.agency.id,
            amount,
            files: [file.id],
          },
        });
      }
      getMovements();
      onModalClose();
      if (searchParams.get("url")) {
        onRedirectModalOpen();
      }
    } catch (error) {
      onError();
      status.updateErrorMessage(error);
    }
  };

  return (
    <VStack paddingX={60}>
      <Helmet>
        <title>Mi perfil</title>
      </Helmet>
      <HStack paddingY={8}>
        <Heading color={colors.secondary} fontSize="2xl">
          Saldo
        </Heading>
      </HStack>
      <HStack space={2} marginBottom={5}>
        <Link to={routes.agencyProfile}>
          <Heading fontSize="lg">Mi perfil</Heading>
        </Link>
        <MdArrowRight color={colors.secondary} size={25} />
        <Heading color={colors.secondary} fontSize="lg">
          Saldos
        </Heading>
      </HStack>
      <VStack
        flex={1}
        backgroundColor={colors.white}
        paddingX={7}
        paddingY={10}>
        <VStack alignItems="center" space={10}>
          <HStack justifyContent="space-between" width="full">
            <Heading color={colors.secondary} fontSize="xl">
              Saldo
            </Heading>
            <Text>{moment().format(moment.HTML5_FMT.DATE)}</Text>
          </HStack>
          <VStack space={5} alignItems="center">
            {isLoadingBalance ? (
              <Spinner size="lg" color={colors.primary} />
            ) : (
              <Heading>{formatToCurrency(balance)}</Heading>
            )}
            <Pressable
              backgroundColor={colors.primary}
              paddingY={2}
              paddingX={5}
              borderRadius={20}
              onPress={onModalOpen}>
              <Text color={colors.white} textAlign="center">
                Recargar
              </Text>
            </Pressable>
          </VStack>
          <VStack maxWidth={500} width="full">
            <Text bold>Movimientos</Text>
            {isLoadingMovements ? (
              <Spinner size="lg" color={colors.primary} marginTop={5} />
            ) : (
              movements.map((movement) => (
                <HStack
                  key={movement.id}
                  alignItems="flex-end"
                  justifyContent="space-between"
                  marginTop={5}
                  paddingBottom={2}
                  borderBottomWidth={1}>
                  <VStack>
                    <Text>{getType(movement)}</Text>
                    <Text>
                      {moment(movement.createdAt).format(
                        moment.HTML5_FMT.DATE.concat(" hh:mm A"),
                      )}
                    </Text>
                  </VStack>
                  <VStack alignItems="flex-end">
                    <Text bold color={getStatusColor(movement.status)}>
                      {getStatus(movement.status)}
                    </Text>
                    <Text>{formatToCurrency(movement.amount)}</Text>
                  </VStack>
                </HStack>
              ))
            )}
          </VStack>
        </VStack>
      </VStack>
      <PaymentModal
        isOpen={isModalOpen}
        onRequestClose={onModalClose}
        label="Valor a recargar"
        amount={0}
        onPay={onRecharge}
        defaultType="transfer"
        hideBalance
        hideCredit
        hideTransfer={false}
        editable
        title="Finalizar recarga"
        isRecharge
      />
      <Modal
        maxWidth={400}
        isOpen={isRedirectModalOpen}
        onRequestClose={onRedirectModalClose}>
        <VStack padding={5} space={3}>
          <Text textAlign="center">¿Desea retonar a la página anterior?</Text>
          <HStack space={5}>
            <Pressable flex={1} onPress={onRedirectModalClose}>
              <Box
                backgroundColor={colors.primary}
                paddingY={2}
                paddingX={5}
                borderRadius="full">
                <Text color={colors.white} textAlign="center">
                  Cerrar
                </Text>
              </Box>
            </Pressable>
            <Pressable
              flex={1}
              onPress={() =>
                navigate(searchParams.get("url"), { state: location.state })
              }>
              <Box
                backgroundColor={colors.primary}
                paddingY={2}
                paddingX={5}
                borderRadius="full">
                <Text color={colors.white} textAlign="center">
                  Retornar
                </Text>
              </Box>
            </Pressable>
          </HStack>
        </VStack>
      </Modal>
    </VStack>
  );
}

export default AgencyProfileBalancePage;
